/**
 * The columns of a structural log: tadpoles, fracture quantities, and the
 * interval columns that carry lithology and fault damage.
 *
 * All of them take the same `top`/`base` pair and the same pixel height, and
 * none of them owns a depth axis. That is what lets them be stacked beside the
 * image and beside an ordinary curve track and stay registered — a column that
 * computed its own scale would drift the moment the window changed.
 */

import type { ReactNode } from "react";
import { type Feature, bearing, tadpole } from "../lib/imagelog";

interface Scale {
  top: number;
  base: number;
  height: number;
}

function yOf({ top, base, height }: Scale, md: number): number {
  return ((md - top) / (base - top || 1)) * height;
}

/**
 * Dip and azimuth in one symbol, at the depth it was measured.
 *
 * Every tadpole is a dot at its dip on a 0–90° scale with a tail pointing at
 * its dip azimuth. Colour carries the kind, so a steep conductive fracture and
 * a steep bedding plane — which sit on top of each other in this track — stay
 * apart.
 */
export function TadpoleTrack({
  features,
  width,
  scale,
  title = "Dip_TRU",
  onSelect,
  selectedId,
}: {
  features: Feature[];
  width: number;
  scale: Scale;
  title?: string;
  onSelect?: (feature: Feature) => void;
  selectedId?: number | null;
}) {
  return (
    <TrackFrame width={width} height={scale.height} title={title} ticks={["0", "45", "90"]}>
      {features.map((feature) => {
        const { x, dx, dy } = tadpole(feature.dip, feature.azimuth, width);
        const y = yOf(scale, feature.md);
        if (y < -4 || y > scale.height + 4) return null;
        const chosen = feature.id === selectedId;
        return (
          <g
            key={feature.id}
            style={{ cursor: onSelect ? "pointer" : undefined }}
            onClick={() => onSelect?.(feature)}
          >
            <line x1={x} y1={y} x2={x + dx} y2={y + dy}
                  stroke={feature.color} strokeWidth={chosen ? 1.8 : 1} />
            <circle cx={x} cy={y} r={chosen ? 3.4 : 2.2}
                    fill={feature.color} stroke={chosen ? "var(--ink)" : "none"}
                    strokeWidth={0.8} />
            <title>
              {feature.kind_label} · {feature.md.toFixed(2)} m · dip {feature.dip.toFixed(1)}° ·
              {" "}azimuth {bearing(feature.azimuth)}°
              {feature.aperture_mm != null && ` · aperture ${feature.aperture_mm.toFixed(2)} mm`}
            </title>
          </g>
        );
      })}
    </TrackFrame>
  );
}

/**
 * A spike per feature, plus the running mean over the same window.
 *
 * Both, not one: the spikes are the measurements and the line is what gets
 * published as a curve, and showing only the line hides that a smooth "high
 * aperture" interval can be three big fractures and forty metres of nothing.
 */
export function SpikeTrack({
  points,
  running,
  width,
  scale,
  title,
  unit,
  max,
  color,
  logScale = false,
}: {
  points: { md: number; value: number; color?: string }[];
  running?: { md: number; value: number }[];
  width: number;
  scale: Scale;
  title: string;
  unit?: string;
  max: number;
  color: string;
  logScale?: boolean;
}) {
  const xOf = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (!logScale) return Math.min(value / max, 1) * width;
    const lo = Math.log10(Math.max(max / 1000, 1e-6));
    const hi = Math.log10(max);
    return Math.min(Math.max((Math.log10(value) - lo) / (hi - lo), 0), 1) * width;
  };

  const line = (running ?? [])
    .filter((point) => Number.isFinite(point.value))
    .map((point) => `${xOf(point.value).toFixed(1)},${yOf(scale, point.md).toFixed(1)}`)
    .join(" ");

  return (
    <TrackFrame
      width={width}
      height={scale.height}
      title={title}
      ticks={["0", unit ?? "", String(max)]}
    >
      {points.map((point, i) => {
        const y = yOf(scale, point.md);
        if (y < 0 || y > scale.height) return null;
        return (
          <line
            key={i}
            x1={0}
            y1={y}
            x2={xOf(point.value)}
            y2={y}
            stroke={point.color ?? color}
            strokeWidth={1}
            opacity={0.75}
          />
        );
      })}
      {line && <polyline points={line} fill="none" stroke={color} strokeWidth={1.4} />}
    </TrackFrame>
  );
}

/** Coloured intervals with a label — lithology, damage zones, cored sections. */
export function IntervalColumn({
  intervals,
  width,
  scale,
  title,
  showLabels = true,
}: {
  intervals: { top_md: number; base_md: number; color: string; label: string; hint?: string }[];
  width: number;
  scale: Scale;
  title: string;
  showLabels?: boolean;
}) {
  return (
    <TrackFrame width={width} height={scale.height} title={title}>
      {intervals.map((interval, i) => {
        const y = yOf(scale, interval.top_md);
        const height = Math.max(yOf(scale, interval.base_md) - y, 1);
        if (y > scale.height || y + height < 0) return null;
        return (
          <g key={i}>
            <rect x={0} y={y} width={width} height={height}
                  fill={interval.color} opacity={0.85} />
            <line x1={0} y1={y} x2={width} y2={y}
                  stroke="var(--line-strong)" strokeWidth={0.5} />
            {/* Label hanya pada pita yang cukup tinggi untuk memuatnya. Teks
                setinggi 9 px di dalam pita 4 px tidak terbaca dan justru
                menutupi pita tetangganya. */}
            {showLabels && height > 14 && (
              <text
                x={width / 2}
                y={y + height / 2 + 3}
                textAnchor="middle"
                style={{
                  font: "500 8.5px var(--font-mono)",
                  fill: "var(--ink)",
                  pointerEvents: "none",
                }}
              >
                {interval.label}
              </text>
            )}
            <title>
              {interval.hint ?? interval.label} · {interval.top_md.toFixed(1)}–
              {interval.base_md.toFixed(1)} m
            </title>
          </g>
        );
      })}
    </TrackFrame>
  );
}

function TrackFrame({
  width,
  height,
  title,
  ticks,
  children,
}: {
  width: number;
  height: number;
  title: string;
  ticks?: string[];
  children: ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "none" }}>
      <div
        style={{
          width,
          borderBottom: "1px solid var(--line)",
          padding: "2px 0",
          textAlign: "center",
          font: "500 9.5px var(--font-mono)",
          color: "var(--ink-4)",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {title}
      </div>
      {ticks && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width,
            padding: "1px 2px",
            font: "400 8px var(--font-mono)",
            color: "var(--ink-6)",
          }}
        >
          {ticks.map((tick, i) => (
            <span key={i}>{tick}</span>
          ))}
        </div>
      )}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        style={{
          background: "var(--panel-inset)",
          borderLeft: "1px solid var(--line)",
          borderRight: "1px solid var(--line)",
        }}
      >
        {children}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Fracture density and mean aperture on a running window, computed here.
 *
 * The server publishes the authoritative curves — those are what the composite
 * log and the CPI sheet read. This exists so that dragging the window slider
 * redraws immediately instead of waiting for a round trip per step; the two use
 * the same definition, and `borehole/processing.py` is where that definition is
 * written down.
 */
export function runningStats(
  features: Feature[],
  top: number,
  base: number,
  window: number,
  step = 0.25,
): { md: number; density: number; aperture: number }[] {
  const sorted = [...features].sort((a, b) => a.md - b.md);
  const out: { md: number; density: number; aperture: number }[] = [];
  const half = window / 2;
  for (let md = top; md <= base; md += step) {
    let count = 0;
    let sum = 0;
    let measured = 0;
    for (const feature of sorted) {
      if (feature.md < md - half) continue;
      if (feature.md > md + half) break;
      count += 1;
      if (feature.aperture_mm != null && Number.isFinite(feature.aperture_mm)) {
        sum += feature.aperture_mm;
        measured += 1;
      }
    }
    out.push({
      md,
      density: count / window,
      aperture: measured ? sum / measured : NaN,
    });
  }
  return out;
}
